import { Fragment, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, MapPinned } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getOfferRegionStats, type EntityStatsParams } from "@/lib/api/stats";

const NON_RENSEIGNEE = "Non renseignée";

// Simulations, cotations et propositions par offre, dépliables par région
// puis par agence. Les simulations restent au niveau de l'offre : un calcul
// non sauvegardé ne porte pas d'agence.
export function OfferRegionStatsCard({ filterParams }: { filterParams: EntityStatsParams }) {
  const [openOffers, setOpenOffers] = useState<Set<number>>(new Set());
  const [openRegions, setOpenRegions] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["offer-region-stats", filterParams],
    queryFn: () => getOfferRegionStats(filterParams),
  });

  const toggle = <T,>(set: Set<T>, key: T) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const exportCsv = useCallback(() => {
    if (!data?.length) return;
    const header = "Offre;Région;Agence;Simulations;Cotations;Propositions\n";
    const rows: string[] = [];
    for (const o of data) {
      rows.push(`${o.simulator_name};;;${o.simulations};${o.cotations};${o.proposals}`);
      for (const r of o.regions) {
        const region = r.group_name ?? NON_RENSEIGNEE;
        rows.push(`${o.simulator_name};${region};;;${r.cotations};${r.proposals}`);
        for (const a of r.agencies) {
          rows.push(`${o.simulator_name};${region};${a.agency_name};;${a.cotations};${a.proposals}`);
        }
      }
    }
    const blob = new Blob(["﻿" + header + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stats-offres-regions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <MapPinned className="h-5 w-5" />
            Statistiques par offre et par région
          </CardTitle>
          <CardDescription>
            Déplier une offre pour la voir par région, puis une région pour la voir par agence.
            Les simulations ne sont comptées qu'au niveau de l'offre : un calcul non sauvegardé n'a pas
            d'agence. Une cotation rattachée à plusieurs agences compte dans chacune.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.length}>
          <Download className="h-4 w-4 mr-1.5" />
          CSV
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data?.length ? (
          <p className="text-center py-6 text-muted-foreground">Aucune donnée disponible</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Offre / région / agence</TableHead>
                <TableHead className="text-right">Simulations</TableHead>
                <TableHead className="text-right">Cotations</TableHead>
                <TableHead className="text-right">Propositions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((o) => {
                const offerOpen = openOffers.has(o.simulator_id);
                return (
                  <Fragment key={o.simulator_id}>
                    <TableRow
                      className={o.regions.length ? "cursor-pointer" : undefined}
                      onClick={() => o.regions.length && setOpenOffers((s) => toggle(s, o.simulator_id))}
                    >
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {o.regions.length ? (
                            offerOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                          ) : (
                            <span className="w-4" />
                          )}
                          {o.simulator_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{o.simulations}</TableCell>
                      <TableCell className="text-right">{o.cotations}</TableCell>
                      <TableCell className="text-right">{o.proposals}</TableCell>
                    </TableRow>
                    {offerOpen &&
                      o.regions.map((r) => {
                        const regionKey = `${o.simulator_id}:${r.group_id ?? "none"}`;
                        const regionOpen = openRegions.has(regionKey);
                        return (
                          <Fragment key={regionKey}>
                            <TableRow
                              className={`bg-muted/30 ${r.agencies.length ? "cursor-pointer" : ""}`}
                              onClick={() => r.agencies.length && setOpenRegions((s) => toggle(s, regionKey))}
                            >
                              <TableCell className="pl-8">
                                <span className="inline-flex items-center gap-1.5">
                                  {r.agencies.length ? (
                                    regionOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                                  ) : (
                                    <span className="w-4" />
                                  )}
                                  {r.group_name ?? <span className="italic text-muted-foreground">{NON_RENSEIGNEE}</span>}
                                </span>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">—</TableCell>
                              <TableCell className="text-right">{r.cotations}</TableCell>
                              <TableCell className="text-right">{r.proposals}</TableCell>
                            </TableRow>
                            {regionOpen &&
                              r.agencies.map((a) => (
                                <TableRow key={`${regionKey}:${a.agency_id}`} className="bg-muted/10">
                                  <TableCell className="pl-16 text-muted-foreground">{a.agency_name}</TableCell>
                                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                                  <TableCell className="text-right">{a.cotations}</TableCell>
                                  <TableCell className="text-right">{a.proposals}</TableCell>
                                </TableRow>
                              ))}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
